import React, { useState, useEffect, useRef } from "react";

/**
 * A flexible tab component for organizing content into switchable sections.
 * 
 * This component provides a clean interface for tabbed navigation, allowing users to
 * switch between different content sections while maintaining the active state.
 * Perfect for organizing complex content like lecture details, course information,
 * or any multi-section content that benefits from a tabbed interface.
 *
 * @typedef {Object} Tab
 * @property {string} id - Unique identifier for the tab
 * @property {string} label - Display text for the tab button
 * @property {React.ReactNode} content - Content to display when tab is active
 * @property {string} [title] - Optional tooltip text for the tab
 * @property {string} [icon] - Optional CSS class for an icon (e.g., "fas fa-home")
 * @property {number} [badge] - Optional badge count to display on the tab
 *
 * @typedef {Object} PageTabsProps
 * @property {Tab[]} tabs - Array of tab objects containing id, label, and content
 * @property {string} [defaultTab] - ID of the tab to show by default
 * @property {(tabId: string) => void} [onTabChange] - Callback fired when tab changes
 * @property {string} [className] - Additional CSS classes to apply to the container
 *
 * @param {PageTabsProps} props - Component props
 * @returns {JSX.Element|null} The rendered tab component, or null if no tabs provided
 *
 * @example
 * // Basic usage with minimal tabs
 * const tabs = [
 *   {
 *     id: 'overview',
 *     label: 'Overview',
 *     icon:  'fal fa-info-circle',
 *     content: <div>overview content</div>
 *   },
 *   {
 *     id: 'curriculum',
 *     label: 'Curriculum',
 *     icon:  'fal fa-edit',
 *     badge: 3,
 *     content: <div>Course curriculum content</div>
 *   }
 * ];
 * 
 * return <PageTabs tabs={tabs} />;
 */
const PageTabs = ({
  tabs = [],
  defaultTab = null,
  activeTab: controlledActiveTab = null,
  onTabChange = null,
  className = "",
  mobileStepper = null,
}) => {
  const [uncontrolledActiveTab, setUncontrolledActiveTab] = useState(
    controlledActiveTab || defaultTab || tabs[0]?.id || null
  );
  const [isSticky, setIsSticky] = useState(false);
  const sentinelRef = useRef(null);
  const isControlled =
    controlledActiveTab !== null && controlledActiveTab !== undefined;
  const activeTab = isControlled ? controlledActiveTab : uncontrolledActiveTab;

  useEffect(() => {
    if (!isControlled) {
      return;
    }

    if (controlledActiveTab !== null && controlledActiveTab !== undefined) {
      setUncontrolledActiveTab(controlledActiveTab);
    } else if (!uncontrolledActiveTab) {
      setUncontrolledActiveTab(defaultTab || tabs[0]?.id || null);
    }
  }, [controlledActiveTab, defaultTab, tabs, isControlled, uncontrolledActiveTab]);

  useEffect(() => {
    const sentinelEl = sentinelRef.current;
    if (!sentinelEl) return;

    const headerHeight = 48;
    const mobileQuery = window.matchMedia("(max-width: 991px)");

    const updateStickyState = () => {
      if (!mobileQuery.matches) {
        setIsSticky(false);
        return;
      }

      const { top } = sentinelEl.getBoundingClientRect();
      setIsSticky(top <= headerHeight);
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!mobileQuery.matches) {
          setIsSticky(false);
          return;
        }

        setIsSticky(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: `-${headerHeight}px 0px 0px 0px`,
      }
    );

    observer.observe(sentinelEl);

    const removeMediaListener = (() => {
      if (mobileQuery.addEventListener) {
        mobileQuery.addEventListener("change", updateStickyState);
        return () => mobileQuery.removeEventListener("change", updateStickyState);
      }

      mobileQuery.addListener(updateStickyState);
      return () => mobileQuery.removeListener(updateStickyState);
    })();

    window.addEventListener("resize", updateStickyState);

    // Run once after mount so we pick up the current scroll position
    updateStickyState();

    return () => {
      observer.disconnect();
      removeMediaListener();
      window.removeEventListener("resize", updateStickyState);
    };
  }, []);

  const handleTabClick = (tabId) => {
    if (!isControlled) {
      setUncontrolledActiveTab(tabId);
    }
    if (onTabChange) {
      onTabChange(tabId);
    }
  };

  const handleSideBar = () => {
    const body = document.body;
    body.classList.toggle("hide-subject-sidebar");
    if (body.classList.contains("hide-subject-sidebar")) {
      body.classList.remove("sub-sidebar-c");
    } else {
      body.classList.add("sub-sidebar-c");
    }
  };

  const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;

  if (!tabs.length) {
    return null;
  }

  return (
    <>
      <div className={`lecture-detail-bottom ${className}`}>
        <div className="lecture-tabs-wrap programme__page">
          <div
            ref={sentinelRef}
            aria-hidden="true"
            style={{
              height: "1px",
              width: "1px",
              opacity: 0,
              pointerEvents: "none",
            }}
          />
          <div className={`course-nav-sticky-wrapper ${isSticky ? 'is-sticky' : ''}`}>
            <ul className="nav nav-pills course-nav-sticky" id="pills-tab" role="tablist">
              {tabs.map((tab) => (
                <li
                  key={tab.id}
                  className="nav-item"
                  title={tab.title}
                  onClick={() => handleTabClick(tab.id)}
                >
                  <span className={`nav-link ${activeTab === tab.id ? "active" : ""}`}>
                    {tab.icon && (
                      <span style={{ position: "relative", display: "inline-block", marginRight: "5px" }}>
                        <i className={tab.icon}></i>
                        {typeof tab.badge === "number" && tab.badge > 0 ? 
                        (
                          <small 
                            className="notify-count"
                            style={{
                              position: "absolute",
                              right: "-0.5em",
                              top: "-0.8em",
                              width: "18px",
                              height: "18px",
                              background: "#C1232C",
                              borderRadius: "100%",
                              border: "1px solid #F3F6F9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "9px",
                              lineHeight: "1",
                              color: "var(--white)",
                              fontWeight: "bold",
                              zIndex: 10,
                            }}
                          >
                            {tab.badge > 9 ? "9+" : tab.badge}
                          </small>
                        )
                      : typeof tab.badge === "string" && tab.badge !== "" ? 
                      (
                        <small 
                            className="notify-count"
                            style={{
                              position: "absolute",
                              right: "-0.5em",
                              top: "-0.8em",
                              width: "18px",
                              height: "18px",
                              background: "#C1232C",
                              borderRadius: "100%",
                              border: "1px solid #F3F6F9",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "9px",
                              lineHeight: "1",
                              color: "var(--white)",
                              fontWeight: "bold",
                              zIndex: 10,
                            }}
                          >
                            {tab.badge > 9 ? "9+" : tab.badge}
                          </small>
                      ) : ""
                      }
                      </span>
                    )}
                    {tab.label}
                  </span>
                </li>
              ))}
            </ul>
            <button
              className="mobile-sidebar-toggle"
              onClick={handleSideBar}
              title="Toggle Sidebar"
              aria-label="Toggle Sidebar"
            >
              <i className="fal fa-bars mobile-sidebar-toggle-icon"></i>
              <i className="fal fa-times mobile-sidebar-toggle-close"></i>
            </button>
          </div>

          {mobileStepper && (
            <>
              {mobileStepper}
            </>
          )}

          <div className="tab-content" id="pills-tabContent">
            <div role="tabpanel" aria-labelledby={`pills-${activeTab}-tab`}>
              {activeTabContent}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PageTabs;
